// ApiDocs.jsx — Public API documentation page for SurFox
// No authentication required. Accessible at /api-docs.

import React, { useState, useRef, useEffect } from 'react';
import { Highlight, themes } from 'prism-react-renderer';
import {
  Copy,
  Check,
  Menu,
  X,
  ExternalLink,
  Key,
  UserPlus,
  Webhook,
  Bell,
  AlertTriangle,
  BookOpen,
  Send,
  Zap,
  Sparkles,
} from 'lucide-react';

// ============================================================================
// CONSTANTS
// ============================================================================

const BASE_URL = 'https://wuuqrdlfgkasnwydyvgk.supabase.co/functions/v1';

const SECTIONS = [
  { id: 'overview',              label: 'Overview',              icon: BookOpen },
  { id: 'authentication',        label: 'Authentication',        icon: Key },
  { id: 'mcp-connector',         label: 'AI Assistant (MCP)',    icon: Sparkles },
  { id: 'inbound-leads',         label: 'Send Leads (Inbound)',  icon: UserPlus },
  { id: 'outbound-overview',     label: 'Outbound Webhooks',     icon: Webhook },
  { id: 'outbound-auth',         label: 'Webhook Auth',          icon: Key },
  { id: 'event-lead-turned-hot', label: 'lead_turned_hot Event', icon: Bell },
  { id: 'planned-events',        label: 'Planned Events',        icon: Sparkles },
  { id: 'test-event',            label: 'Test Fires',            icon: Send },
  { id: 'error-handling',        label: 'Error Handling',        icon: AlertTriangle },
];

// ============================================================================
// CODE BLOCK COMPONENT
// ============================================================================

function CodeBlock({ code, language = 'bash', title }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-900 my-4">
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
          <span className="text-xs font-medium text-slate-400">{title}</span>
          <button
            onClick={handleCopy}
            className="flex items-center space-x-1 text-xs text-slate-400 hover:text-white transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
      )}
      <Highlight theme={themes.nightOwl} code={code.trim()} language={language}>
        {({ style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className="overflow-x-auto p-4 text-sm leading-relaxed"
            style={{ ...style, background: 'transparent', margin: 0 }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
}

// ============================================================================
// TABBED CODE BLOCK — cURL / JavaScript / Python
// ============================================================================

function TabbedCode({ examples }) {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const tabs = examples.map((e) => e.label);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(examples[active].code.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-900 my-4">
      <div className="flex items-center justify-between px-4 py-0 bg-slate-800 border-b border-slate-700">
        <div className="flex">
          {tabs.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActive(i)}
              className={`px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
                active === i
                  ? 'text-white border-indigo-400'
                  : 'text-slate-400 border-transparent hover:text-slate-200'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center space-x-1 text-xs text-slate-400 hover:text-white transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      <Highlight
        theme={themes.nightOwl}
        code={examples[active].code.trim()}
        language={examples[active].language || 'bash'}
      >
        {({ style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className="overflow-x-auto p-4 text-sm leading-relaxed"
            style={{ ...style, background: 'transparent', margin: 0 }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
}

// ============================================================================
// HTTP METHOD BADGE
// ============================================================================

function MethodBadge({ method }) {
  const colors = {
    GET: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    POST: 'bg-blue-100 text-blue-800 border-blue-200',
    PUT: 'bg-amber-100 text-amber-800 border-amber-200',
    DELETE: 'bg-red-100 text-red-800 border-red-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${colors[method] || ''}`}>
      {method}
    </span>
  );
}

// ============================================================================
// FIELD TABLE
// ============================================================================

function FieldTable({ fields }) {
  return (
    <div className="overflow-x-auto my-4">
      <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-4 py-2 font-semibold text-slate-700">Field</th>
            <th className="text-left px-4 py-2 font-semibold text-slate-700">Type</th>
            <th className="text-left px-4 py-2 font-semibold text-slate-700">Required</th>
            <th className="text-left px-4 py-2 font-semibold text-slate-700">Description</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0">
              <td className="px-4 py-2 font-mono text-xs text-indigo-700">{f.name}</td>
              <td className="px-4 py-2 text-slate-500">{f.type}</td>
              <td className="px-4 py-2">
                {f.required ? (
                  <span className="text-red-600 font-medium text-xs">Required</span>
                ) : (
                  <span className="text-slate-400 text-xs">Optional</span>
                )}
              </td>
              <td className="px-4 py-2 text-slate-600">{f.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// SECTION WRAPPER
// ============================================================================

function Section({ id, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-16">
      {children}
    </section>
  );
}

function SectionTitle({ children }) {
  return <h2 className="text-2xl font-bold text-slate-900 mb-2">{children}</h2>;
}

function Endpoint({ method, path }) {
  return (
    <div className="flex items-center space-x-3 my-4 p-3 bg-slate-50 rounded-lg border border-slate-200 font-mono text-sm">
      <MethodBadge method={method} />
      <span className="text-slate-700">{path}</span>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function ApiDocs() {
  const [mobileNav, setMobileNav] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const mainRef = useRef(null);

  // Intersection observer for sidebar highlighting
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileNav(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ── HEADER ──────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
          <div className="flex items-center space-x-3">
            <img src="/newSurFoxLogo1.png" alt="SurFox AI" className="h-8 object-contain" />
            <span className="text-slate-300">|</span>
            <span className="text-slate-600 text-sm font-medium">API Reference</span>
          </div>
          <div className="flex items-center space-x-4">
            <a
              href="https://surfox.ai"
              className="hidden sm:flex items-center text-sm text-slate-500 hover:text-slate-900 transition-colors"
            >
              surfox.ai <ExternalLink className="w-3 h-3 ml-1" />
            </a>
            <button
              onClick={() => setMobileNav(!mobileNav)}
              className="lg:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100"
            >
              {mobileNav ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto flex">
        {/* ── SIDEBAR ─────────────────────────────────────────────── */}
        <aside
          className={`
            fixed lg:sticky top-16 left-0 z-30 h-[calc(100vh-4rem)] w-64 bg-white border-r border-slate-200
            overflow-y-auto shrink-0 transition-transform lg:translate-x-0
            ${mobileNav ? 'translate-x-0 shadow-xl' : '-translate-x-full'}
          `}
        >
          <nav className="p-4 space-y-1">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`
                  w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors text-left
                  ${activeSection === id
                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }
                `}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* ── MAIN CONTENT ────────────────────────────────────────── */}
        <main ref={mainRef} className="flex-1 min-w-0 px-4 sm:px-8 lg:px-12 py-10">
          {/* Intro */}
          <div className="mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              SurFox API Reference
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl">
              Send leads into SurFox, let the AI run the SMS conversation, and receive a
              webhook the moment a lead is qualified as hot — with the full conversation context.
            </p>
            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-700">
                <span className="font-semibold">Base URL:</span>{' '}
                <code className="px-1.5 py-0.5 bg-slate-200 rounded text-xs font-mono">
                  {BASE_URL}
                </code>
              </p>
              <p className="text-sm text-slate-500 mt-2">
                All endpoints listed below are Supabase Edge Functions. Append the function name to the base URL.
              </p>
            </div>
          </div>

          {/* ── 1. OVERVIEW ───────────────────────────────────────── */}
          <Section id="overview">
            <SectionTitle>Overview</SectionTitle>
            <p className="text-slate-600 mb-4">
              SurFox is an AI-powered SMS lead qualification platform. The integration surface is two
              halves of a loop:
            </p>

            <div className="grid sm:grid-cols-2 gap-4 my-6">
              <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex items-center space-x-2 mb-2">
                  <UserPlus className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-semibold text-slate-800">1. Inbound</h3>
                </div>
                <p className="text-sm text-slate-600">
                  You POST a lead to <code className="px-1 py-0.5 bg-white rounded text-xs">/inbound-leads</code>.
                  SurFox creates the lead, opens an SMS conversation, and the AI takes over.
                </p>
              </div>
              <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex items-center space-x-2 mb-2">
                  <Webhook className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-semibold text-slate-800">2. Outbound</h3>
                </div>
                <p className="text-sm text-slate-600">
                  When the lead is qualified as hot, SurFox POSTs the
                  {' '}<code className="px-1 py-0.5 bg-white rounded text-xs">lead_turned_hot</code> event
                  to your webhook URL with the full conversation, scoring, and judge verdict.
                </p>
              </div>
            </div>

            <p className="text-slate-600">
              Outbound webhooks carry the complete payload — name, phone, email, every message in the
              conversation — so you don't need a follow-up call to get the data. Pass the payload
              straight to your CRM or alerting workflow.
            </p>
          </Section>

          {/* ── 2. AUTHENTICATION ─────────────────────────────────── */}
          <Section id="authentication">
            <SectionTitle>Authentication</SectionTitle>
            <p className="text-slate-600 mb-4">
              All inbound API calls require an API key sent as the{' '}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">X-SurFox-API-Key</code> header.
              Keys start with the prefix{' '}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">sfx_</code> and are scoped
              to a single tenant. Generate keys in the SurFox dashboard under{' '}
              <span className="font-medium">Settings → API Keys</span>.
            </p>

            <div className="my-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-900 font-medium mb-1">Header format</p>
              <code className="block text-xs font-mono text-amber-900 bg-amber-100 px-3 py-2 rounded">
                X-SurFox-API-Key: sfx_your_api_key_here
              </code>
            </div>

            <h3 className="text-lg font-semibold text-slate-800 mt-8 mb-2">Validate a key</h3>
            <p className="text-slate-600 mb-4">
              Optional sanity-check endpoint. POSTs an API key and returns the tenant it's scoped to.
              Useful during initial wiring; not required for subsequent calls.
            </p>

            <Endpoint method="POST" path="/zapier-auth" />

            <FieldTable
              fields={[
                { name: 'api_key', type: 'string', required: true, desc: 'Your SurFox API key (starts with sfx_)' },
              ]}
            />

            <TabbedCode
              examples={[
                {
                  label: 'cURL',
                  language: 'bash',
                  code: `curl -X POST ${BASE_URL}/zapier-auth \\
  -H "Content-Type: application/json" \\
  -d '{"api_key": "sfx_your_api_key_here"}'`,
                },
                {
                  label: 'JavaScript',
                  language: 'javascript',
                  code: `const response = await fetch("${BASE_URL}/zapier-auth", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ api_key: "sfx_your_api_key_here" })
});
const data = await response.json();
// { tenant_id: "uuid", tenant_name: "Your Company", key_name: "..." }`,
                },
                {
                  label: 'Python',
                  language: 'python',
                  code: `import requests

response = requests.post(
    "${BASE_URL}/zapier-auth",
    json={"api_key": "sfx_your_api_key_here"}
)
data = response.json()
# {"tenant_id": "uuid", "tenant_name": "Your Company", "key_name": "..."}`,
                },
              ]}
            />

            <CodeBlock
              title="Response 200"
              language="json"
              code={`{
  "tenant_id": "550e8400-e29b-41d4-a716-446655440000",
  "tenant_name": "Your Company",
  "key_name": "Production Zap"
}`}
            />

            <CodeBlock
              title="Response 401"
              language="json"
              code={`{ "error": "Invalid or expired API key" }`}
            />
          </Section>

          {/* ── 2b. MCP CONNECTOR ─────────────────────────────────── */}
          <Section id="mcp-connector">
            <SectionTitle>AI Assistant (MCP)</SectionTitle>
            <p className="text-slate-600 mb-4">
              SurFox offers a read-only connector for AI assistants such as Claude, built on the{' '}
              <a href="https://modelcontextprotocol.io" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                Model Context Protocol
              </a>{' '}
              (MCP). Any account administrator can connect their own assistant to their own SurFox
              account and ask it questions about their leads, conversations, campaigns and usage,
              without logging into SurFox first.
            </p>

            <div className="my-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-indigo-900 font-medium mb-1">Connector URL</p>
              <code className="block text-xs font-mono text-indigo-900 bg-indigo-100 px-3 py-2 rounded">
                https://mcp.surfox.ai/mcp
              </code>
            </div>

            <h3 className="text-lg font-semibold text-slate-800 mt-8 mb-2">Connecting</h3>
            <p className="text-slate-600 mb-4">
              In Claude, go to Settings → Connectors → Add custom connector, and enter the URL above.
              Claude will open a browser to sign in with your own SurFox login, then show you exactly
              what the assistant will be able to read before you approve it. No API key, password or
              setup step is needed beyond that sign-in.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-8 mb-2">What the assistant can do</h3>
            <p className="text-slate-600 mb-4">This connector is read-only. It cannot send a text, change a record, or see billing. It has five tools:</p>
            <ul className="space-y-2 text-slate-600 mb-4 list-disc list-inside">
              <li><span className="font-medium text-slate-800">Find a lead</span> — by phone or email. Phone numbers come back masked to the last four digits; email addresses come back in full.</li>
              <li><span className="font-medium text-slate-800">List recent hot leads</span> — leads marked hot in a given window.</li>
              <li><span className="font-medium text-slate-800">Read a lead conversation</span> — the message history with one lead. Numbers typed inside message text are masked the same way, where they can be recognized.</li>
              <li><span className="font-medium text-slate-800">List active campaigns</span> — names and talk-track type of currently active campaigns.</li>
              <li><span className="font-medium text-slate-800">Get usage summary</span> — texts used this billing cycle, plan limits, and web chat usage. No dollar amounts or payment details.</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-8 mb-2">Access and revoking</h3>
            <p className="text-slate-600 mb-4">
              A connection lasts 90 days and must be reconnected after that. You can remove access at
              any time from <span className="font-medium">Settings → Integrations → API Keys → Connected
              assistants</span>, which also shows what each connected assistant has read.
            </p>

            <div className="my-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-900 font-medium mb-1">Your data, your assistant&apos;s rules</p>
              <p className="text-xs text-amber-900">
                Once a message, lead or campaign name reaches your connected assistant, it is handled by
                that assistant&apos;s own provider under that provider&apos;s own terms, not SurFox&apos;s.
                See our{' '}
                <a href="https://www.getsurfox.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">
                  Privacy Policy
                </a>{' '}
                for details.
              </p>
            </div>
          </Section>

          {/* ── 3. INBOUND LEADS ──────────────────────────────────── */}
          <Section id="inbound-leads">
            <SectionTitle>Send Leads (Inbound)</SectionTitle>
            <p className="text-slate-600 mb-4">
              POST a lead to a campaign. SurFox creates the lead in{' '}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">Cold Lead</code> status,
              opens an SMS conversation record, and the AI begins outreach according to the
              campaign's talk track.
            </p>

            <Endpoint method="POST" path="/inbound-leads" />

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-2">Body</h3>
            <FieldTable
              fields={[
                { name: 'phone',         type: 'string', required: true,  desc: 'Phone number — accepts any common format; auto-normalized to E.164 (e.g. "555-123-4567" → "+15551234567")' },
                { name: 'campaign_id',   type: 'uuid',   required: true,  desc: 'Campaign to assign the lead to. May also be passed as ?campaign_id=… query param, or resolved automatically from tags (see below).' },
                { name: 'first_name',    type: 'string', required: true,  desc: "Lead's first name. Required — used for personalization in every outbound SMS." },
                { name: 'last_name',     type: 'string', required: false, desc: "Lead's last name" },
                { name: 'email',         type: 'string', required: false, desc: "Lead's email address" },
                { name: 'tags',          type: 'string[] or string', required: false, desc: 'Tags — array or comma-separated. Used for tag-based campaign auto-routing if campaign_id is omitted.' },
                { name: 'custom_fields', type: 'object', required: false, desc: 'Free-form JSON. Stored on the lead row and referenceable in the AI prompt.' },
                { name: 'notes',         type: 'string', required: false, desc: 'Free-form notes about the lead' },
              ]}
            />

            <div className="my-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="font-semibold text-slate-800 text-sm mb-2">Campaign resolution order</p>
              <ol className="text-sm text-slate-600 space-y-1 list-decimal pl-5">
                <li><code className="px-1 py-0.5 bg-white rounded text-xs">campaign_id</code> in the request body (highest priority)</li>
                <li><code className="px-1 py-0.5 bg-white rounded text-xs">?campaign_id=</code> in the query string</li>
                <li>Tag-to-campaign mapping — if you supply <code className="px-1 py-0.5 bg-white rounded text-xs">tags</code>, the first tag that matches an active mapping in the SurFox dashboard wins.</li>
              </ol>
              <p className="text-sm text-slate-500 mt-2">
                If none of the three resolves a campaign, the request returns{' '}
                <code className="px-1 py-0.5 bg-white rounded text-xs">400</code>.
              </p>
            </div>

            <TabbedCode
              examples={[
                {
                  label: 'cURL',
                  language: 'bash',
                  code: `curl -X POST ${BASE_URL}/inbound-leads \\
  -H "X-SurFox-API-Key: sfx_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "campaign_id": "b1c2d3e4-f5a6-7890-abcd-ef1234567890",
    "first_name": "Sarah",
    "last_name": "Mitchell",
    "phone": "+15551234567",
    "email": "sarah@example.com",
    "custom_fields": {
      "Company Name": "Acme Corp",
      "Job Title": "Office Manager"
    },
    "notes": "Referred by John"
  }'`,
                },
                {
                  label: 'JavaScript',
                  language: 'javascript',
                  code: `const response = await fetch("${BASE_URL}/inbound-leads", {
  method: "POST",
  headers: {
    "X-SurFox-API-Key": "sfx_your_api_key_here",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    campaign_id: "b1c2d3e4-f5a6-7890-abcd-ef1234567890",
    first_name: "Sarah",
    last_name: "Mitchell",
    phone: "+15551234567",
    email: "sarah@example.com"
  })
});
const lead = await response.json();`,
                },
                {
                  label: 'Python',
                  language: 'python',
                  code: `import requests

response = requests.post(
    "${BASE_URL}/inbound-leads",
    headers={
        "X-SurFox-API-Key": "sfx_your_api_key_here",
        "Content-Type": "application/json"
    },
    json={
        "campaign_id": "b1c2d3e4-f5a6-7890-abcd-ef1234567890",
        "first_name": "Sarah",
        "last_name": "Mitchell",
        "phone": "+15551234567",
        "email": "sarah@example.com"
    }
)
lead = response.json()`,
                },
              ]}
            />

            <h3 className="text-lg font-semibold text-slate-800 mt-8 mb-2">Responses</h3>

            <CodeBlock
              title="201 — New lead created"
              language="json"
              code={`{
  "id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "name": "Sarah Mitchell",
  "first_name": "Sarah",
  "last_name": "Mitchell",
  "phone": "+15551234567",
  "email": "sarah@example.com",
  "status": "Cold Lead",
  "campaign_id": "b1c2d3e4-f5a6-7890-abcd-ef1234567890",
  "created_at": "2026-05-02T22:31:00Z",
  "already_exists": false
}`}
            />

            <CodeBlock
              title="200 — Duplicate (lead already exists in this campaign)"
              language="json"
              code={`{
  "id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "name": "Sarah Mitchell",
  "first_name": "Sarah",
  "last_name": "Mitchell",
  "phone": "+15551234567",
  "email": "sarah@example.com",
  "status": "Hot Lead",
  "campaign_id": "b1c2d3e4-f5a6-7890-abcd-ef1234567890",
  "created_at": "2026-05-02T22:31:00Z",
  "already_exists": true
}`}
            />
            <p className="text-sm text-slate-500 -mt-2 mb-4">
              Dedup is keyed on (phone, campaign_id) within a tenant. The same phone in a different
              campaign creates a fresh lead.
            </p>

            <CodeBlock
              title="400 — Missing required field"
              language="json"
              code={`{ "error": "phone is required", "field": "phone" }`}
            />

            <CodeBlock
              title="401 — Invalid API key"
              language="json"
              code={`{ "error": "Invalid or expired API key" }`}
            />

            <CodeBlock
              title="404 — Campaign not found / not in your tenant"
              language="json"
              code={`{ "error": "Campaign not found or access denied", "field": "campaign_id" }`}
            />
          </Section>

          {/* ── 4. OUTBOUND OVERVIEW ──────────────────────────────── */}
          <Section id="outbound-overview">
            <SectionTitle>Outbound Webhooks</SectionTitle>
            <p className="text-slate-600 mb-4">
              SurFox POSTs an event to your webhook URL when a lead is qualified as hot. The payload
              includes the full lead, the entire conversation, scoring, and the AI judge verdict —
              everything you need to route the lead into a CRM, page a sales rep, or trigger an
              automation, with no follow-up API call.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-2">Registration</h3>
            <p className="text-slate-600 mb-4">
              Register webhooks in the SurFox dashboard under{' '}
              <span className="font-medium">Settings → Outbound Webhooks</span>. There you'll
              configure:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-4">
              <li><span className="font-medium">Integration type</span> — HighLevel, Salesforce, Podio, Bullhorn, or Generic Webhook</li>
              <li><span className="font-medium">Webhook URL</span> — the HTTPS endpoint SurFox will POST to (HTTP is rejected)</li>
              <li><span className="font-medium">Authentication</span> — none, bearer token, or custom header (see next section)</li>
              <li><span className="font-medium">Subscribed events</span> — currently a single event:{' '}
                <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">lead_turned_hot</code>
              </li>
            </ul>
            <p className="text-slate-600 mb-4">
              The Settings panel includes a <span className="font-medium">Test</span> button that
              fires a synthetic <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">lead_turned_hot.test</code>{' '}
              event against your URL so you can verify wiring before going live. Recent deliveries
              (real and test) appear in the same panel.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-8 mb-2">Headers SurFox sends</h3>
            <CodeBlock
              language="text"
              title="Outbound request headers"
              code={`Content-Type:    application/json
User-Agent:      SurFox-Webhooks/1.0
X-SurFox-Event:  lead_turned_hot`}
            />
            <p className="text-slate-600 mb-4">
              Plus any auth header you configured (see{' '}
              <button onClick={() => scrollTo('outbound-auth')} className="text-indigo-600 hover:underline">
                Webhook Auth
              </button>
              ).
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-8 mb-2">Retries and dead-letter</h3>
            <p className="text-slate-600 mb-4">
              SurFox makes <span className="font-medium">up to 5 delivery attempts</span> per
              event. Any non-2xx response, or a network error / timeout after 10 seconds,
              schedules the next attempt with exponential backoff. If the 5th attempt also fails,
              the queue row is moved to <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">dead_letter</code>{' '}
              and no further delivery is attempted (worst-case total elapsed time before
              dead-letter ≈ 15 minutes plus per-attempt latency).
            </p>

            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-2 font-semibold text-slate-700">Attempt #</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-700">Action on failure</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['1 (initial)', 'wait 1 minute, retry'],
                    ['2',           'wait 2 minutes, retry'],
                    ['3',           'wait 4 minutes, retry'],
                    ['4',           'wait 8 minutes, retry'],
                    ['5 (final)',   'dead-lettered — no further delivery attempted'],
                  ].map(([attempt, action]) => (
                    <tr key={attempt} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-2 font-mono text-xs text-slate-700">{attempt}</td>
                      <td className="px-4 py-2 text-slate-600">{action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-600 mb-4">
              The payload is rebuilt from live database state right before each delivery, so a
              retry sent ten minutes after the initial fire will reflect the lead's current state
              (latest message count, most recent scoring).
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-8 mb-2">Disabled connections</h3>
            <p className="text-slate-600 mb-4">
              If you disable a connection in the dashboard, in-flight queue rows are paused —
              SurFox rechecks once an hour and resumes delivery the moment you re-enable it. No
              attempts are counted against the retry budget while paused.
            </p>
          </Section>

          {/* ── 5. OUTBOUND AUTH ──────────────────────────────────── */}
          <Section id="outbound-auth">
            <SectionTitle>Webhook Auth</SectionTitle>
            <p className="text-slate-600 mb-4">
              Three modes are supported when configuring a webhook in the SurFox dashboard.
              Whichever you pick, SurFox attaches the corresponding header(s) to every delivery
              (real and test).
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-2">No auth</h3>
            <p className="text-slate-600 mb-2">
              No additional header is added. Use this when the webhook URL itself contains a secret
              token (e.g. Zapier's{' '}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">hooks.zapier.com/hooks/standard/&lt;id&gt;/&lt;token&gt;</code>{' '}
              pattern).
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-2">Bearer token</h3>
            <p className="text-slate-600 mb-2">
              You supply a token in the dashboard. SurFox sends:
            </p>
            <CodeBlock
              language="text"
              code={`Authorization: Bearer <your-token>`}
            />

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-2">Custom header</h3>
            <p className="text-slate-600 mb-2">
              You supply both a header name and its value in the dashboard. SurFox sends:
            </p>
            <CodeBlock
              language="text"
              code={`<your-header-name>: <your-header-value>`}
            />
            <p className="text-slate-600 mb-4">
              Useful for vendor-specific schemes — for example HighLevel's{' '}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">X-API-Key</code>, or an
              internal gateway token.
            </p>
          </Section>

          {/* ── 6. EVENT: lead_turned_hot ─────────────────────────── */}
          <Section id="event-lead-turned-hot">
            <SectionTitle>
              <code className="text-2xl font-mono text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                lead_turned_hot
              </code>
            </SectionTitle>

            <p className="text-slate-600 mb-4">
              The only outbound event currently emitted. Fires when a lead transitions to{' '}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">Hot Lead</code> status —
              by AI scoring, the AI judge, manual escalation, or being created directly in Hot Lead
              state via inbound API.
            </p>

            <p className="text-slate-600 mb-4">
              Most fires hit your endpoint within 60 seconds of the status change. If the dispatcher
              is backed up, retries can take longer; the{' '}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">fired_at</code> field is
              the actual moment SurFox sent the request.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-8 mb-2">Payload</h3>

            <CodeBlock
              language="json"
              title="lead_turned_hot — full payload"
              code={`{
  "event_type": "lead_turned_hot",
  "event_id": "8a2c1f4e-9b1d-4c8b-aa3f-7e1f2c9b0e44",
  "fired_at": "2026-05-02T23:15:00.482Z",

  "tenant": {
    "id": "46f58bba-b709-4460-8df1-ee61f0d42c57",
    "name": "Your Company"
  },

  "lead": {
    "id": "bd6cac99-bb60-41e6-892f-812c3a8531b3",
    "first_name": "Sarah",
    "last_name": "Mitchell",
    "phone": "+15551234567",
    "email": "sarah@example.com",
    "status": "Hot Lead",
    "hot_reason": "Confirmed budget and timeline within first 4 messages",
    "marked_hot_at": "2026-05-02T23:14:55Z",
    "source": "webhook",
    "created_at": "2026-05-02T22:31:00Z",
    "estimated_pipeline_value": null,
    "lead_detail_url": "https://surfox.ai/lead/bd6cac99-bb60-41e6-892f-812c3a8531b3"
  },

  "campaign": {
    "id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "name": "Spring 2026 Outreach",
    "talk_track_type": "acquiring_clients"
  },

  "appointment": {
    "booked": false,
    "datetime": null
  },

  "scoring": {
    "current_ai_score": 87,
    "weighted_score": 92,
    "latest_message_scores": {
      "hesitation_score": 18,
      "urgency_score": 82,
      "contextual_sentiment_score": 75,
      "ai_confidence_score": 90,
      "scored_at": "2026-05-02T23:14:50Z"
    }
  },

  "judge": {
    "is_hot": true,
    "temperature": "hot",
    "confidence": 0.94,
    "reasoning": "Lead confirmed they have approval to spend in Q3 and gave a specific decision date."
  },

  "conversation": {
    "message_count": 6,
    "first_message_at": "2026-05-02T22:32:10Z",
    "last_message_at":  "2026-05-02T23:14:48Z",
    "kb_sources_used": [
      "Pricing FAQ — May 2026",
      "Implementation Timeline"
    ],
    "messages": [
      {
        "direction": "outbound",
        "sender":    "SurFox AI",
        "body":      "Hi Sarah, this is Alex from Your Company...",
        "timestamp": "2026-05-02T22:32:10Z",
        "channel":   "sms"
      },
      {
        "direction": "inbound",
        "sender":    "Sarah Mitchell",
        "body":      "Yes I'm interested — what does pricing look like?",
        "timestamp": "2026-05-02T22:38:01Z",
        "channel":   "sms"
      }
    ]
  }
}`}
            />

            <h3 className="text-lg font-semibold text-slate-800 mt-8 mb-2">Field reference</h3>

            <FieldTable
              fields={[
                { name: 'event_type',                       type: 'string',     required: true,  desc: 'Always "lead_turned_hot" for real fires; "lead_turned_hot.test" for synthetic test fires from the dashboard.' },
                { name: 'event_id',                         type: 'uuid',       required: true,  desc: 'Unique id for this delivery. Stable across retries — use it for idempotency on your side.' },
                { name: 'fired_at',                         type: 'iso8601',    required: true,  desc: 'Server-side timestamp of when SurFox sent the request.' },
                { name: 'tenant.id',                        type: 'uuid',       required: true,  desc: 'Your SurFox tenant ID.' },
                { name: 'tenant.name',                      type: 'string',     required: false, desc: 'Tenant display name (may be null).' },
                { name: 'lead.id',                          type: 'uuid',       required: true,  desc: 'SurFox lead ID.' },
                { name: 'lead.first_name',                  type: 'string',     required: false, desc: 'First name as captured at intake (may be null).' },
                { name: 'lead.last_name',                   type: 'string',     required: false, desc: 'Last name as captured at intake (may be null).' },
                { name: 'lead.phone',                       type: 'string',     required: true,  desc: 'E.164 phone number.' },
                { name: 'lead.email',                       type: 'string',     required: false, desc: 'Email as captured at intake (may be null).' },
                { name: 'lead.status',                      type: 'string',     required: true,  desc: 'Always "Hot Lead" at fire time.' },
                { name: 'lead.hot_reason',                  type: 'string',     required: false, desc: "Short freeform string set when the lead was promoted to Hot. Common values: a judge reasoning summary (e.g. 'Lead explicitly agreed to a call'), 'appointment_booked', 'manual_test', or null if not set." },
                { name: 'lead.marked_hot_at',               type: 'iso8601',    required: false, desc: 'Moment the lead was marked hot.' },
                { name: 'lead.source',                      type: 'string',     required: false, desc: 'Original intake source — "webhook", "zapier", a CSV import, etc.' },
                { name: 'lead.created_at',                  type: 'iso8601',    required: true,  desc: 'When the lead row was created.' },
                { name: 'lead.estimated_pipeline_value',    type: 'number',     required: false, desc: 'Optional deal value if your campaign captures one.' },
                { name: 'lead.lead_detail_url',             type: 'string',     required: true,  desc: 'Direct link into the SurFox dashboard for this lead.' },
                { name: 'campaign',                         type: 'object|null',required: false, desc: 'Campaign block, or null if the lead has no campaign.' },
                { name: 'campaign.id',                      type: 'uuid',       required: false, desc: 'Campaign ID.' },
                { name: 'campaign.name',                    type: 'string',     required: false, desc: 'Campaign display name.' },
                { name: 'campaign.talk_track_type',         type: 'string',     required: false, desc: 'Talk-track variant configured in the campaign.' },
                { name: 'appointment.booked',               type: 'boolean',    required: true,  desc: 'True if a scheduled or confirmed appointment exists for this lead.' },
                { name: 'appointment.datetime',             type: 'iso8601',    required: false, desc: 'Scheduled time of the appointment, or null if none.' },
                { name: 'scoring.current_ai_score',         type: 'number',     required: false, desc: 'Current cumulative AI score on the lead.' },
                { name: 'scoring.weighted_score',           type: 'number',     required: false, desc: 'Score on the most recent inbound message that produced one.' },
                { name: 'scoring.latest_message_scores',    type: 'object|null',required: false, desc: 'Detailed sub-scores from the latest scored inbound message; null if nothing has been scored yet.' },
                { name: 'judge.is_hot',                     type: 'boolean',    required: false, desc: 'AI judge verdict.' },
                { name: 'judge.temperature',                type: 'string',     required: false, desc: 'Judge temperature label — "hot", "warm", "cold", etc.' },
                { name: 'judge.confidence',                 type: 'number',     required: false, desc: 'Judge confidence in [0, 1].' },
                { name: 'judge.reasoning',                  type: 'string',     required: false, desc: 'Judge reasoning string. Often the highest-signal field for routing decisions on your side.' },
                { name: 'conversation.message_count',       type: 'number',     required: true,  desc: 'Total inbound + outbound messages in the conversation.' },
                { name: 'conversation.first_message_at',    type: 'iso8601',    required: false, desc: 'Timestamp of the first message.' },
                { name: 'conversation.last_message_at',     type: 'iso8601',    required: false, desc: 'Timestamp of the last message.' },
                { name: 'conversation.kb_sources_used',     type: 'string[]',   required: true,  desc: 'Deduplicated names of knowledge-base chunks the AI referenced across all outbound messages.' },
                { name: 'conversation.messages',            type: 'object[]',   required: true,  desc: 'Full message history in chronological order. Each entry has direction, sender, body, timestamp, channel. A message body that could not be decrypted is emitted as { body: null, decryption_failed: true } rather than failing the payload.' },
              ]}
            />
          </Section>

          {/* ── 7. PLANNED EVENTS (COMING SOON) ───────────────────── */}
          <Section id="planned-events">
            <SectionTitle>Planned Events (Coming Soon)</SectionTitle>

            <div className="my-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-indigo-900">
                These events are <span className="font-medium">planned but not yet emitted</span>.
                You can subscribe to them today via the Settings UI — when implemented, they will
                fire automatically to your existing webhook connections without any
                reconfiguration on your end.
              </p>
            </div>

            <p className="text-slate-600 mb-4">
              The names below use dot-notation to visually distinguish them from the live{' '}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">lead_turned_hot</code>{' '}
              event. Final naming and payload shapes will be finalized when each event ships.
            </p>

            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-2 font-semibold text-slate-700">Event</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-700">Trigger</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['lead.status_changed',     'Any lead status transition (Cold → Warm, Warm → Hot, etc.)'],
                    ['lead.created',            'New lead added to a campaign from any source'],
                    ['lead.replied',            'Lead sends an inbound SMS'],
                    ['conversation.completed',  'AI conversation finishes'],
                    ['lead.opted_out',          'Lead texts STOP or otherwise opts out'],
                    ['lead.call_logged',        'Sales rep logs a call attempt against the lead'],
                    ['lead.converted',          'Lead is marked Won or Lost'],
                  ].map(([event, trigger]) => (
                    <tr key={event} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">
                        <code className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded">
                          {event}
                        </code>
                      </td>
                      <td className="px-4 py-2 text-slate-600">{trigger}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ── 8. TEST FIRES ─────────────────────────────────────── */}
          <Section id="test-event">
            <SectionTitle>Test Fires</SectionTitle>

            <p className="text-slate-600 mb-4">
              Each connection in the dashboard has a <span className="font-medium">Test</span> button
              that POSTs a synthetic payload to your webhook URL. Same shape as a real{' '}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">lead_turned_hot</code>{' '}
              event, but with placeholder data and an explicit{' '}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">test_metadata</code>{' '}
              block so your automations can detect (and ignore) test traffic.
            </p>

            <p className="text-slate-600 mb-4">
              Two differences from a real fire:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-4">
              <li><code className="px-1 py-0.5 bg-slate-100 rounded text-xs">event_type</code> is{' '}
                <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">"lead_turned_hot.test"</code>{' '}
                (suffixed)
              </li>
              <li>A <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">test_metadata</code> block is appended at the top level</li>
            </ul>

            <CodeBlock
              language="json"
              title="lead_turned_hot.test — extra fields"
              code={`{
  "event_type": "lead_turned_hot.test",
  // ... identical shape to the real event ...
  "test_metadata": {
    "connection_id":    "f1e2d3c4-b5a6-7890-abcd-ef1234567890",
    "integration_type": "generic_webhook",
    "integration_name": "Production webhook (live)",
    "note": "event_type ends in .test — safe to ignore in production automations"
  }
}`}
            />

            <div className="my-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-700">
                <span className="font-medium">Recommended filter on your side:</span> branch on{' '}
                <code className="px-1 py-0.5 bg-white rounded text-xs">event_type === "lead_turned_hot"</code>{' '}
                exactly. The <code className="px-1 py-0.5 bg-white rounded text-xs">.test</code>{' '}
                suffix lets you wire and validate automations without polluting your CRM with fake leads.
              </p>
            </div>
          </Section>

          {/* ── 9. ERROR HANDLING ─────────────────────────────────── */}
          <Section id="error-handling">
            <SectionTitle>Error Handling</SectionTitle>
            <p className="text-slate-600 mb-4">
              Inbound API errors return a JSON body with an{' '}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">error</code> field, plus
              an optional <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">field</code>{' '}
              field on validation failures.
            </p>

            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-2 font-semibold text-slate-700">Status</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-700">Meaning</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-700">Example body</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['400', 'Bad Request',                 '{"error": "phone is required", "field": "phone"}'],
                    ['401', 'Unauthorized',                '{"error": "Invalid or expired API key"}'],
                    ['403', 'Tenant suspended',            '{"error": "Account suspended. Contact support@getsurfox.com."}'],
                    ['404', 'Campaign or resource not found', '{"error": "Campaign not found or access denied", "field": "campaign_id"}'],
                    ['405', 'Method not allowed',          '{"error": "Method not allowed"}'],
                    ['500', 'Server error',                '{"error": "Internal server error"}'],
                  ].map(([code, meaning, example]) => (
                    <tr key={code} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                          code === '400' ? 'bg-amber-100 text-amber-800' :
                          code === '401' ? 'bg-red-100 text-red-800' :
                          code === '403' ? 'bg-red-100 text-red-800' :
                          code === '404' ? 'bg-slate-100 text-slate-800' :
                          code === '405' ? 'bg-slate-100 text-slate-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {code}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-medium text-slate-700">{meaning}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-500 break-all">{example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-600 mb-4">
              Note: a duplicate inbound submission is <span className="font-medium">not</span> an
              error — it returns <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">200</code>{' '}
              with <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">already_exists: true</code>{' '}
              and the existing lead's row.
            </p>
          </Section>

          {/* ── FOOTER ────────────────────────────────────────────── */}
          <footer className="mt-20 pt-8 border-t border-slate-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-slate-500">
                  Need help?{' '}
                  <a href="mailto:support@surfox.ai" className="text-indigo-600 hover:underline">
                    support@surfox.ai
                  </a>
                </p>
              </div>
              <a
                href="https://zapier.com/apps/surfox/integrations"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-4 py-2 bg-[#FF4A00] hover:bg-[#E64400] text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Zap className="w-4 h-4" />
                <span>View on Zapier</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <p className="text-xs text-slate-400 mt-6 mb-4">
              &copy; {new Date().getFullYear()} SurFox AI. All rights reserved.
            </p>
          </footer>
        </main>
      </div>

      {/* Mobile nav overlay */}
      {mobileNav && (
        <div className="fixed inset-0 z-20 bg-black/30 lg:hidden" onClick={() => setMobileNav(false)} />
      )}
    </div>
  );
}
